import { TriangleAlert } from 'lucide-react'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { reportRenderError } from '@/lib/report-error'

interface ErrorBoundaryProps {
  children: ReactNode
  /** Identifica em qual nível o erro ocorreu nos relatórios. */
  scope: 'root' | 'request-step'
  /** UI alternativa. Quando ausente, usa o aviso padrão. */
  fallback?: ReactNode
  /** Rótulo do botão de recuperação. */
  retryLabel?: string
  /** Ação de recuperação. Quando ausente, recarrega a página. */
  onRetry?: () => void
}

interface ErrorBoundaryState {
  hasError: boolean
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportRenderError(error, this.props.scope, info.componentStack)
  }

  private handleRetry = () => {
    if (this.props.onRetry) {
      this.setState({ hasError: false })
      this.props.onRetry()
      return
    }

    window.location.reload()
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    if (this.props.fallback) {
      return this.props.fallback
    }

    return (
      <div className="w-full flex flex-col items-center justify-center gap-4 py-16 px-6 text-center">
        <div className="flex items-center gap-2 font-semibold text-destructive">
          <TriangleAlert size={20} />
          <p>Algo deu errado ao exibir esta etapa</p>
        </div>
        <p className="text-foreground/80 max-w-150">
          Não conseguimos carregar esta parte da tela. Seus dados não foram
          perdidos. Tente novamente e, se o problema continuar, entre em contato
          com a secretaria da sua instituição.
        </p>
        <Button
          type="button"
          variant="light"
          className="w-50"
          onClick={this.handleRetry}
        >
          {this.props.retryLabel ?? 'Tentar novamente'}
        </Button>
      </div>
    )
  }
}
